import React, { useEffect, useState } from 'react';
import { X, Loader2, Send, Ban, MailWarning } from 'lucide-react';
import type { DemandaEsteira } from '../../../types';
import { FILAS } from './filas';

export type AcaoDocumento = 'registrar_envio' | 'nao_enviar' | 'cancelar';

interface Props {
    demanda: DemandaEsteira | null;
    acao: AcaoDocumento;
    onClose: () => void;
    /** Recebe a data (ISO) no envio, ou o motivo nas outras duas. */
    onConfirmar: (valor: string) => Promise<void>;
}

const META: Record<AcaoDocumento, {
    titulo: string; icone: React.ReactNode; rotulo: string; botao: string;
    ajuda: string; perigoso?: boolean;
}> = {
    registrar_envio: {
        titulo: 'Registrar envio ao cliente',
        icone: <Send className="w-4 h-4 text-primary" />,
        rotulo: 'Data em que o documento foi enviado',
        botao: 'Registrar envio',
        // O prazo é calculado pelo banco a partir desta data — por isso ela é
        // editável: o envio pode ter acontecido ontem e ser registrado hoje.
        ajuda: 'É esta data que inicia o prazo (5 dias úteis na mora, 15 dias corridos na advertência). Informe o dia real do envio, não o de hoje, se forem diferentes.',
    },
    nao_enviar: {
        titulo: 'Não será enviado ao cliente',
        icone: <MailWarning className="w-4 h-4 text-amber-600" />,
        rotulo: 'Por que este documento não será enviado?',
        botao: 'Registrar',
        ajuda: 'Isto é um desfecho, não um adiamento: a demanda sai da fila de envio e o motivo fica no histórico.',
    },
    cancelar: {
        titulo: 'Cancelar documento',
        icone: <Ban className="w-4 h-4 text-red-600" />,
        rotulo: 'Motivo do cancelamento',
        botao: 'Cancelar documento',
        ajuda: 'O documento é apagado no Autentique se ninguém assinou, ou anulado se já houver assinatura — quem decide é o sistema, olhando o estado real.',
        perigoso: true,
    },
};

/** Hoje em 'YYYY-MM-DD', no fuso local — o input date espera esse formato. */
const hojeLocal = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Coleta o que falta para executar uma ação de documento direto do painel:
 * a data no registro de envio, o motivo nas outras duas.
 */
const AcaoDocumentoModal: React.FC<Props> = ({ demanda, acao, onClose, onConfirmar }) => {
    const meta = META[acao];
    const [valor, setValor] = useState('');
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        setValor(acao === 'registrar_envio' ? hojeLocal() : '');
    }, [demanda?.id, acao]);

    useEffect(() => {
        if (!demanda) return;
        const aoTeclar = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', aoTeclar);
        return () => window.removeEventListener('keydown', aoTeclar);
    }, [demanda, onClose]);

    if (!demanda) return null;

    const confirmar = async () => {
        setSalvando(true);
        try {
            // Meio-dia evita que o fuso jogue a data um dia para trás — mesmo
            // cuidado do resto do módulo.
            await onConfirmar(acao === 'registrar_envio' ? new Date(`${valor}T12:00:00`).toISOString() : valor);
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} role="presentation">
            <div
                className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden"
                onClick={e => e.stopPropagation()}
                role="dialog" aria-modal="true" aria-labelledby="acao-doc-titulo"
            >
                <div className="flex items-start justify-between gap-4 p-5 border-b border-gray-100 dark:border-gray-700">
                    <div className="min-w-0">
                        <h2 id="acao-doc-titulo" className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            {meta.icone} {meta.titulo}
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                            {FILAS[demanda.fila].titulo} · {demanda.cliente_nome || 'Cliente sem nome'}
                            {demanda.uc_numero ? ` · UC ${demanda.uc_numero}` : ''}
                        </p>
                    </div>
                    <button onClick={onClose} aria-label="Fechar" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 shrink-0">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-3">
                    <label htmlFor="acao-doc-valor" className="block text-xs font-bold text-gray-600 dark:text-gray-300">
                        {meta.rotulo}
                    </label>
                    {acao === 'registrar_envio' ? (
                        <input
                            id="acao-doc-valor"
                            type="date"
                            autoFocus
                            value={valor}
                            max={hojeLocal()}
                            onChange={e => setValor(e.target.value)}
                            className="px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                    ) : (
                        <textarea
                            id="acao-doc-valor"
                            autoFocus
                            rows={3}
                            value={valor}
                            onChange={e => setValor(e.target.value)}
                            placeholder={acao === 'cancelar' ? 'Ex.: cliente quitou o débito antes do envio.' : 'Ex.: cliente sem e-mail e sem WhatsApp válido.'}
                            className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                    )}
                    <p className="text-[11px] text-gray-400 dark:text-gray-500">{meta.ajuda}</p>
                </div>

                <div className="flex justify-end gap-2 p-5 border-t border-gray-100 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-900/30">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                        Voltar
                    </button>
                    <button
                        onClick={confirmar}
                        disabled={salvando || !valor.trim()}
                        className={`px-4 py-2 text-sm font-bold text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 ${
                            meta.perigoso ? 'bg-red-600 hover:bg-red-700' : 'bg-primary hover:bg-primary/90'
                        }`}
                    >
                        {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        {meta.botao}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AcaoDocumentoModal;
